import { TierPeriod, } from 'features/premium/entities';
import { createSelector } from 'reselect';
import { Platform } from 'react-native';
import { formatPlural, getIapSubscriptionPrices } from 'utils';
import { selectAvailableIAPSubscriptionsWithPrices, selectShouldShowStripe, selectStripePriceMap, } from 'features/premium/selectors';
import { selectIsUserFreeTier } from 'features/premium/selectors/tier';
import checkFeatureFlag from 'utils/featureFlags';
import { getDiscountFromSubscription, getOfferingForPlatform, getPrices, shouldDisplayPromotionInFeed, } from './entities';
import { buildIntroGiftPromotion } from './introGiftPromotion';
import { getProductName } from './utils';
export const selectIntroGiftPromotion = createSelector([
    (store) => store.utils.featureFlags,
    (store) => store.user.user.guessedHomeCountry,
    selectShouldShowStripe,
    selectIsUserFreeTier,
    (store) => store.user.user.emmaProStatus.usedFreeTrial,
    (store) => store.user.user.emmaProStatus.eligibleFreeTrialDays,
    (store) => store.user.user.firstConnectionAt,
    selectStripePriceMap,
    (store) => store.deviceSettings?.introGiftFeedDismissedAt,
], (featureFlags, guessedHomeCountry, shouldShowStripe, isFreeTier, usedFreeTrial, eligibleFreeTrialDays, firstConnectionAt, stripePriceMap, introGiftFeedDismissedAt) => {
    const offerFlagPayload = featureFlags.ab_intro_gift_feed?.extra;
    const offer = offerFlagPayload?.[guessedHomeCountry];
    if (!checkFeatureFlag(featureFlags, 'ab_intro_gift_feed') ||
        !offer ||
        !shouldShowStripe ||
        !isFreeTier ||
        usedFreeTrial ||
        !eligibleFreeTrialDays ||
        !firstConnectionAt ||
        !stripePriceMap) {
        return null;
    }
    return buildIntroGiftPromotion({
        offer,
        firstConnectionAt,
        stripePriceMap,
        feedCardDismissedAt: introGiftFeedDismissedAt ?? null,
    });
});
const selectPromotions = createSelector([
    (state) => state.promotionsState.promotions,
    selectIntroGiftPromotion,
], (promotions, introGiftPromotion) => introGiftPromotion ? [introGiftPromotion, ...promotions] : promotions);
export const selectFeedPromotion = createSelector([
    selectPromotions,
    (_, currentPromotionId) => currentPromotionId,
], (promotions, currentPromotionId) => {
    if (currentPromotionId != null) {
        return (promotions.find((promotion) => promotion.id === currentPromotionId) ??
            null);
    }
    return (promotions.find((promotion) => shouldDisplayPromotionInFeed(promotion)) ??
        null);
});
export const selectPromotionById = createSelector([selectPromotions, (_, promotionId) => promotionId], (promotions, promotionId) => promotions.find((promotion) => promotion.id === promotionId) ?? null);
const selectIapSubscriptions = (store) => store.promotionsState.iapSubscriptions;
/**
 * This selector essentially takes in the IAP Subscriptions
 * combining them with our API promotions
 * to decide whether we can show the user a promo
 */
export const selectPromotionForPlatform = createSelector([
    selectIapSubscriptions,
    selectAvailableIAPSubscriptionsWithPrices,
    selectPromotions,
    (_, promotionId) => promotionId,
    (store) => store.iapSubscriptions.isEligibleForIntroOffer,
    selectShouldShowStripe,
    (store) => store.stripe.priceMap,
    (store) => store.user.user.emmaProStatus.usedFreeTrial,
    (store) => store.user.user.emmaProStatus.eligibleFreeTrialDays,
], (iapSubscriptions, allSubscriptions, promotions, currentPromotionId, isEligibleForIntroOffer, shouldShowStripe, stripePriceMap, usedFreeTrial, eligibleFreeTrialDays) => {
    const currentPromotion = promotions.find((promotion) => promotion.id === currentPromotionId);
    if (!currentPromotion) {
        return undefined;
    }
    const platformOffering = getOfferingForPlatform(currentPromotion, Platform.OS, shouldShowStripe && stripePriceMap != null, eligibleFreeTrialDays);
    if (!platformOffering)
        return null;
    const matchedSubscription = platformOffering.type === 'stripe' && stripePriceMap
        ? Object.values(stripePriceMap[platformOffering.period]).find((product) => product.id === platformOffering.productId)
        : getIapSubscriptionPrices(iapSubscriptions[platformOffering.productId]);
    if (!matchedSubscription)
        return null;
    const matchedDiscount = getDiscountFromSubscription(matchedSubscription, currentPromotion);
    const monthlySubscription = matchedSubscription.platform === 'stripe'
        ? stripePriceMap?.[TierPeriod.monthly]?.[matchedSubscription.tier]
        : allSubscriptions.find((subscription) => subscription.tier === matchedSubscription.tier &&
            subscription.period === TierPeriod.monthly);
    const formattedPrices = getPrices(matchedSubscription, platformOffering.period === TierPeriod.yearly
        ? monthlySubscription || null
        : null, matchedDiscount, platformOffering.period, platformOffering.type === 'stripe'
        ? !usedFreeTrial
        : isEligibleForIntroOffer);
    if (!formattedPrices)
        return null;
    return {
        availableTo: currentPromotion.availableTo,
        discount: platformOffering.discount,
        productName: getProductName(matchedSubscription),
        period: 
        // Note, this is not necessarily the same as the subscription period.
        // For example we could choose to offer 3 months for £0.99 on a yearly plan (in theory)
        formattedPrices.discountedPeriod === TierPeriod.yearly
            ? formatPlural('year', 'years', formattedPrices.numberOfPeriods)
            : formatPlural('month', 'months', formattedPrices.numberOfPeriods),
        freeTrialDays: formattedPrices.canUseFreeTrial &&
            platformOffering.freeTrialDays &&
            platformOffering.freeTrialDays > 0
            ? platformOffering.freeTrialDays
            : null,
        matchedDiscount,
        matchedSubscription,
        formattedPrices: {
            ...formattedPrices,
            percentOff: platformOffering.discount && platformOffering.type === 'stripe'
                ? platformOffering.discount * 100
                : formattedPrices.percentOff,
        },
    };
});
